/** The four corridors, used by both the hotel and the experience filters. */
export const CITIES = [
  { id: 'cdmx', label: 'Mexico City', country: 'Mexico' },
  { id: 'bogota', label: 'Bogotá', country: 'Colombia' },
  { id: 'sao-paulo', label: 'São Paulo', country: 'Brazil' },
  { id: 'santa-cruz', label: 'Santa Cruz', country: 'Bolivia' },
] as const

export type CityId = (typeof CITIES)[number]['id']

export function cityLabel(id: string): string {
  return CITIES.find((city) => city.id === id)?.label ?? id
}

export const AMENITIES = [
  'Free cancellation',
  'Breakfast',
  'Pool',
  'Wi-Fi',
  'Gym',
  'Airport transfer',
  'Kitchen',
] as const

export type Amenity = (typeof AMENITIES)[number]

export interface Stay {
  readonly slug: string
  readonly name: string
  readonly city: CityId
  readonly area: string
  /** Nightly rate in whole dollars — every derived price comes from this. */
  readonly nightly: number
  readonly score: number
  readonly verdict: string
  readonly reviews: number
  readonly blurb: string
  readonly amenities: readonly Amenity[]
  readonly image: string
}

/** Hotels pay 3% back, per the per-product cashback model. */
export const HOTEL_CASHBACK = 0.03
export const DEFAULT_NIGHTS = 3

export const STAYS: readonly Stay[] = [
  {
    slug: 'casa-arrieta', name: 'Casa Arrieta', city: 'cdmx',
    area: 'Polanco · 0.8 km from centre', nightly: 186, score: 9.3,
    verdict: 'Exceptional', reviews: 412, image: 'travel/hotels/casa-arrieta.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Wi-Fi', 'Gym', 'Airport transfer'],
    blurb: 'A restored 1940s house on a quiet street, ten minutes from Polanco’s restaurants and twenty from the airport. Rooms face an inner courtyard, so the traffic never reaches you.',
  },
  {
    slug: 'hotel-mirasol', name: 'Hotel Mirasol', city: 'cdmx',
    area: 'Cuauhtémoc · rooftop pool', nightly: 157, score: 9.1,
    verdict: 'Excellent', reviews: 388, image: 'travel/hotels/hotel-mirasol.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Pool', 'Wi-Fi', 'Gym'],
    blurb: 'A concrete-and-glass hotel built around a lap pool, five minutes from Paseo de la Reforma. The kitchen stays open late, which matters after a night flight.',
  },
  {
    slug: 'patio-catedral', name: 'Patio Catedral', city: 'cdmx',
    area: 'Centro Histórico · roof terrace', nightly: 208, score: 9.4,
    verdict: 'Exceptional', reviews: 297, image: 'travel/hotels/patio-catedral.webp',
    amenities: ['Breakfast', 'Wi-Fi', 'Airport transfer'],
    blurb: 'A nineteenth-century building behind the Cathedral, stripped back to brick and Shaker furniture. The roof terrace looks straight over the Zócalo.',
  },
  {
    slug: 'casa-jacaranda', name: 'Casa Jacaranda', city: 'cdmx',
    area: 'Roma Norte · quiet street', nightly: 133, score: 8.9,
    verdict: 'Very good', reviews: 214, image: 'travel/hotels/casa-jacaranda.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Wi-Fi', 'Kitchen'],
    blurb: 'Five rooms in a 1913 house named after the cook who ran it for four decades. Breakfast is served in the garden and there is no front desk to queue at.',
  },
  {
    slug: 'la-candelaria', name: 'Posada La Candelaria', city: 'bogota',
    area: 'La Candelaria · colonial courtyard', nightly: 74, score: 9.2,
    verdict: 'Excellent', reviews: 341, image: 'travel/hotels/la-candelaria.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Wi-Fi', 'Kitchen'],
    blurb: 'A colonial house around a planted courtyard, two streets from Plaza de Bolívar. Thick walls, so the mornings are quiet even in the old town.',
  },
  {
    slug: 'quinta-usaquen', name: 'Quinta Usaquén', city: 'bogota',
    area: 'Usaquén · near the Sunday market', nightly: 96, score: 8.8,
    verdict: 'Very good', reviews: 188, image: 'travel/hotels/quinta-usaquen.webp',
    amenities: ['Free cancellation', 'Wi-Fi', 'Gym', 'Airport transfer'],
    blurb: 'A straightforward hotel in Usaquén with rooms that get the morning sun. The Sunday market is a five-minute walk and the airport road is quick from here.',
  },
  {
    slug: 'vila-madalena', name: 'Casa Madalena', city: 'sao-paulo',
    area: 'Vila Madalena · near the bars', nightly: 118, score: 9.0,
    verdict: 'Excellent', reviews: 264, image: 'travel/hotels/vila-madalena.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Wi-Fi', 'Kitchen'],
    blurb: 'A converted townhouse on a side street in Vila Madalena, far enough from the bars to sleep and close enough to walk back from them.',
  },
  {
    slug: 'urubo', name: 'Urubó Jardín', city: 'santa-cruz',
    area: 'Urubó · pool and gardens', nightly: 88, score: 9.1,
    verdict: 'Excellent', reviews: 176, image: 'travel/hotels/urubo.webp',
    amenities: ['Free cancellation', 'Breakfast', 'Pool', 'Wi-Fi', 'Airport transfer'],
    blurb: 'Low buildings around a pool on the Urubó side of the river, twenty minutes from the centre. Families come here for a week at a time.',
  },
]

export function findStay(slug: string): Stay | undefined {
  return STAYS.find((stay) => stay.slug === slug)
}

export interface FlightLeg {
  readonly from: string
  readonly to: string
  readonly depart: string
  readonly arrive: string
  readonly duration: string
  readonly aircraft: string
}

export interface Flight {
  readonly slug: string
  readonly route: string
  readonly airline: string
  readonly time: string
  readonly price: string
  /** Base fare in dollars, before taxes and extras. */
  readonly fare: number
  readonly taxes: number
  readonly back: string
  readonly cabin: string
  readonly bag: string
  readonly legs: readonly FlightLeg[]
  /** Which corridor it lands in. Explicit, so nothing has to infer it. */
  readonly destination: CityId
  /** A shot of where the flight lands, not of the aircraft. */
  readonly image: string
}

/** Flights pay 1% back, not the 3% the flat travel programme used to promise. */
export const FLIGHT_CASHBACK = 0.01

export const FLIGHTS: readonly Flight[] = [
  {
    slug: 'jfk-mex',
    destination: 'cdmx',
    image: 'travel/cities/mexico-city.webp',
    route: 'JFK → MEX',
    airline: 'Aeroméxico AM 405',
    time: '08:15 · 5h 40m · direct',
    price: '$184',
    fare: 148,
    taxes: 36,
    back: '$1.84',
    cabin: 'Main cabin',
    bag: 'One carry-on and one checked bag',
    legs: [
      {
        from: 'JFK · New York',
        to: 'MEX · Mexico City',
        depart: '12 Oct · 08:15',
        arrive: '12 Oct · 11:55',
        duration: '5h 40m',
        aircraft: 'Boeing 737-800',
      },
    ],
  },
  {
    slug: 'jfk-bog',
    destination: 'bogota',
    image: 'travel/cities/bogota.webp',
    route: 'JFK → BOG',
    airline: 'Avianca AV 245',
    time: '09:40 · 5h 55m · direct',
    price: '$246',
    fare: 198,
    taxes: 48,
    back: '$2.46',
    cabin: 'Economy',
    bag: 'One carry-on · checked bag extra',
    legs: [
      {
        from: 'JFK · New York',
        to: 'BOG · Bogotá',
        depart: '12 Oct · 09:40',
        arrive: '12 Oct · 14:35',
        duration: '5h 55m',
        aircraft: 'Airbus A320neo',
      },
    ],
  },
  {
    slug: 'mia-gru',
    destination: 'sao-paulo',
    image: 'travel/cities/sao-paulo.webp',
    route: 'MIA → GRU',
    airline: 'LATAM LA 8181',
    time: '22:10 · 8h 20m · direct',
    price: '$412',
    fare: 338,
    taxes: 74,
    back: '$4.12',
    cabin: 'Economy',
    bag: 'One carry-on and one checked bag',
    legs: [
      {
        from: 'MIA · Miami',
        to: 'GRU · São Paulo',
        depart: '12 Oct · 22:10',
        arrive: '13 Oct · 07:30',
        duration: '8h 20m',
        aircraft: 'Boeing 787-9',
      },
    ],
  },
  {
    slug: 'mia-vvi',
    destination: 'santa-cruz',
    image: 'travel/cities/santa-cruz.webp',
    route: 'MIA → VVI',
    airline: 'Boliviana OB 701',
    time: '01:05 · 6h 50m · 1 stop',
    price: '$498',
    fare: 412,
    taxes: 86,
    back: '$4.98',
    cabin: 'Economy',
    bag: 'One carry-on and one checked bag',
    legs: [
      {
        from: 'MIA · Miami',
        to: 'VVI · Santa Cruz',
        depart: '12 Oct · 01:05',
        arrive: '12 Oct · 06:20',
        duration: '5h 15m',
        aircraft: 'Boeing 767-300',
      },
      {
        from: 'VVI · Santa Cruz',
        to: 'CBB · Cochabamba',
        depart: '12 Oct · 07:25',
        arrive: '12 Oct · 08:00',
        duration: '35m · connection',
        aircraft: 'Boeing 737-700',
      },
    ],
  },
]

export function findFlight(slug: string): Flight | undefined {
  return FLIGHTS.find((flight) => flight.slug === slug)
}
